import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { adsApi } from '@/api/client';
import { BudgetRuleCampaignAssociations } from '@/components/BudgetRuleCampaignAssociations';
import { BudgetRuleLinkedCampaignsChips } from '@/components/BudgetRuleLinkedCampaignsChips';
import { useBudgetRuleAssociatedCampaigns } from '@/hooks/useBudgetRuleAssociatedCampaigns';
import type { CampaignAdProduct, SpCampaignNormalized } from '@/types/spCampaigns';
import { campaignAdProductToBudgetRuleApiProduct } from '@/types/spCampaigns';
import {
  budgetRuleKindLabel,
  budgetRuleRowToFormState,
  buildUpdateBudgetRulePayload,
  detectBudgetRuleKind,
  type BudgetRuleFormState,
} from '@/utils/spBudgetRules';

const DAY_OPTIONS = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

type BudgetRuleEditModalProps = {
  profileId: string;
  product: CampaignAdProduct;
  row: Record<string, unknown> | null;
  isOpen: boolean;
  onClose: () => void;
  profileCampaigns?: SpCampaignNormalized[];
  onAssociationsChanged?: () => void;
  excludeCampaignIds?: string[];
  /** When false, hides linked campaigns UI (e.g. campaign details page). */
  showCampaignAssociations?: boolean;
};

export function BudgetRuleEditModal({
  profileId,
  product,
  row,
  isOpen,
  onClose,
  profileCampaigns = [],
  onAssociationsChanged,
  excludeCampaignIds = [],
  showCampaignAssociations = true,
}: BudgetRuleEditModalProps) {
  const qc = useQueryClient();
  const [form, setForm] = useState<BudgetRuleFormState | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && row) {
      setForm(budgetRuleRowToFormState(row));
      setError(null);
    }
  }, [isOpen, row]);

  const ruleId = typeof row?.ruleId === 'string' ? row.ruleId : '';

  const {
    associated,
    isLoading: associationsLoading,
    error: associationsError,
    queryKey: associationsQueryKey,
  } = useBudgetRuleAssociatedCampaigns(
    profileId,
    product,
    ruleId,
    showCampaignAssociations && isOpen && !!ruleId
  );

  if (!isOpen || !row || !form) return null;

  const kind = detectBudgetRuleKind(row);

  const patch = (partial: Partial<BudgetRuleFormState>) => {
    setForm((prev) => (prev ? { ...prev, ...partial } : prev));
  };

  const toggleDay = (day: string, checked: boolean) => {
    setForm((prev) => {
      if (!prev) return prev;
      const next = checked
        ? [...prev.daysOfWeek, day].filter((d, i, a) => a.indexOf(d) === i)
        : prev.daysOfWeek.filter((d) => d !== day);
      return { ...prev, daysOfWeek: next.sort() };
    });
  };

  const budgetRuleApiProduct = campaignAdProductToBudgetRuleApiProduct(product);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const { payload, error: buildError } = buildUpdateBudgetRulePayload(row, form);
    if (buildError || !payload) {
      setError(buildError || 'Invalid form');
      return;
    }
    setSubmitting(true);
    try {
      const res = await adsApi.updateBudgetRules(profileId, budgetRuleApiProduct, {
        budgetRulesDetails: [payload],
      });
      if (!res.success) throw new Error('Failed to update budget rule');
      await qc.invalidateQueries({ queryKey: ['budget-rules', budgetRuleApiProduct, profileId] });
      onClose();
    } catch (err) {
      setError((err as Error).message || 'Failed to update budget rule');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-surface rounded-xl max-w-lg w-full p-6 shadow-xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Edit budget rule</h3>
          <button type="button" onClick={onClose} className="text-chrome-label hover:text-foreground-muted">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-xs text-foreground-subtle font-mono mb-4">{ruleId}</p>

        <form onSubmit={submit} className="space-y-3">
          <Field label="Rule type">
            <input
              type="text"
              readOnly
              value={budgetRuleKindLabel(kind)}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-page text-foreground-muted"
            />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Rule name">
              <input
                value={form.ruleName}
                onChange={(e) => patch({ ruleName: e.target.value })}
                className="w-full border border-border rounded-lg px-3 py-2 text-sm"
                disabled={submitting}
              />
            </Field>
            <Field label="Increase by %">
              <input
                type="number"
                min="0"
                step="0.01"
                value={form.increasePct}
                onChange={(e) => patch({ increasePct: e.target.value })}
                className="w-full border border-border rounded-lg px-3 py-2 text-sm"
                disabled={submitting}
              />
            </Field>
          </div>

          <Field label="Rule state">
            <select
              value={form.ruleState}
              onChange={(e) => patch({ ruleState: e.target.value as 'ACTIVE' | 'PAUSED' })}
              className="w-full border border-border rounded-lg px-3 py-2 text-sm"
              disabled={submitting}
            >
              <option value="ACTIVE">Active</option>
              <option value="PAUSED">Paused</option>
            </select>
          </Field>

          {kind === 'EVENT_DAILY' && (
            <Field label="Event ID">
              <input
                value={form.eventId}
                readOnly
                className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-page text-foreground-muted"
              />
            </Field>
          )}

          {kind !== 'EVENT_DAILY' && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Start date">
                <input
                  type="date"
                  value={form.startDate}
                  onChange={(e) => patch({ startDate: e.target.value })}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm"
                  disabled={submitting}
                />
              </Field>
              <Field label="End date">
                <input
                  type="date"
                  value={form.endDate}
                  onChange={(e) => patch({ endDate: e.target.value })}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm"
                  disabled={submitting}
                />
              </Field>
            </div>
          )}

          {kind === 'SCHEDULE_WEEKLY' && (
            <Field label="Days of week">
              <div className="flex flex-wrap gap-3">
                {DAY_OPTIONS.map((d) => (
                  <label key={d} className="text-sm text-foreground-muted flex items-center gap-1">
                    <input
                      type="checkbox"
                      checked={form.daysOfWeek.includes(d)}
                      onChange={(e) => toggleDay(d, e.target.checked)}
                      disabled={submitting}
                    />
                    {d.slice(0, 3)}
                  </label>
                ))}
              </div>
            </Field>
          )}

          {kind === 'PERFORMANCE_DAILY' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Metric">
                <input
                  type="text"
                  readOnly
                  value={form.metricName}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-page"
                />
              </Field>
              <Field label="Threshold">
                <input
                  type="number"
                  step="0.01"
                  value={form.threshold}
                  onChange={(e) => patch({ threshold: e.target.value })}
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm"
                  disabled={submitting}
                />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Comparison operator">
                  <select
                    value={form.comparisonOperator}
                    onChange={(e) => patch({ comparisonOperator: e.target.value })}
                    className="w-full border border-border rounded-lg px-3 py-2 text-sm"
                    disabled={submitting}
                  >
                    <option value="LESS_THAN_OR_EQUAL_TO">Less than or equal to</option>
                    <option value="LESS_THAN">Less than</option>
                    <option value="GREATER_THAN_OR_EQUAL_TO">Greater than or equal to</option>
                    <option value="GREATER_THAN">Greater than</option>
                  </select>
                </Field>
              </div>
            </div>
          )}

          {showCampaignAssociations && ruleId ? (
            <div className="rounded-xl border border-border p-3 space-y-3">
              <div>
                <p className="text-xs font-medium text-foreground-muted mb-1.5">Associated campaigns</p>
                <BudgetRuleLinkedCampaignsChips
                  associated={associated}
                  profileCampaigns={profileCampaigns}
                  isLoading={associationsLoading}
                  error={associationsError}
                />
              </div>
              <BudgetRuleCampaignAssociations
                profileId={profileId}
                product={product}
                ruleId={ruleId}
                profileCampaigns={profileCampaigns}
                associated={associated}
                isLoading={associationsLoading}
                loadError={associationsError}
                excludeCampaignIds={excludeCampaignIds}
                onAssociationsChanged={() => {
                  void qc.invalidateQueries({
                    queryKey: ['budget-rules', budgetRuleApiProduct, profileId],
                  });
                  void qc.invalidateQueries({ queryKey: associationsQueryKey });
                  onAssociationsChanged?.();
                }}
              />
            </div>
          ) : null}

          {error && <p className="text-sm text-error">{error}</p>}

          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 border border-border rounded-lg py-2 text-sm"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 bg-brand text-white rounded-lg py-2 text-sm font-medium disabled:opacity-50"
              disabled={submitting}
            >
              {submitting ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-xs text-foreground-muted mb-1">{label}</label>
      {children}
    </div>
  );
}
